import React, { useEffect, useState } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, FileText, Download, Eye, ShieldCheck } from "lucide-react";
import { api, apiError, API, authToken } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export default function DriveShare() {
  const { token } = useParams();
  const { user } = useApp();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (user === undefined) return;
    if (!user) {
      try { sessionStorage.setItem("rud_post_login_redirect", `/drive/s/${token}`); } catch (e) { /* noop */ }
      nav("/auth");
      return;
    }
    api.get(`/drive/shared/${token}`)
      .then(({ data }) => setState({ loading: false, ...data }))
      .catch((e) => setState({ loading: false, error: apiError(e.response?.data?.detail) }));
  }, [user, token, nav]);

  const download = async () => {
    try {
      const res = await fetch(`${API}/drive/shared/${token}/download?mode=original`, { headers: { Authorization: `Bearer ${authToken.get()}` }, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = href; a.download = state.file?.filename || "file";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
    } catch { toast.error("This link is view-only or no longer available."); }
  };

  if (state.loading || user === undefined) return <div className="min-h-screen grid place-items-center text-sec"><HeartLoader /></div>;

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-md rud-card rounded-2xl p-6 text-center" data-testid="drive-share-page">
        {state.error ? (
          <p className="text-sec">{state.error}</p>
        ) : (
          <>
            <div className="w-12 h-12 mx-auto grid place-items-center rounded-full bg-brand/15 text-brand mb-3"><FileText size={24} /></div>
            <h1 className="font-serif text-2xl">{state.file?.filename}</h1>
            <p className="text-[12px] text-sec mt-1">Shared by {state.shared_by} · {state.permission} access</p>
            {state.file?.ai_summary && <p className="text-sm text-sec mt-3">{state.file.ai_summary}</p>}
            <div className="mt-5">
              {state.permission === "view" && !state.is_owner ? (
                <p className="inline-flex items-center gap-1.5 text-sm text-sec"><Eye size={15} /> View-only — download isn't permitted</p>
              ) : (
                <button data-testid="shared-download" onClick={download} className="btn-primary rounded-full h-11 px-6 text-sm inline-flex items-center gap-2"><Download size={16} /> Download file</button>
              )}
            </div>
            <p className="text-[11px] text-sec mt-4 flex items-center justify-center gap-1"><ShieldCheck size={12} /> Secured by My Kind Smart Drive</p>
          </>
        )}
      </div>
    </div>
  );
}
